/**
 * Employee Stats Cards Component
 * Displays clickable stat cards for filtering employees
 */
import React from 'react';
import { Users, UserCheck, Clock, AlertTriangle, UserX, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

// Tab configuration
const TABS = [
  { id: 'total', label: 'Total Employees', key: 'total', color: 'blue', icon: Users },
  { id: 'active', label: 'Active', key: 'active', color: 'emerald', icon: UserCheck },
  { id: 'pending_auth', label: 'Pending Auth', key: 'pending_auth', color: 'amber', icon: Clock },
  { id: 'on_probation', label: 'On Probation', key: 'probation', color: 'purple', icon: AlertTriangle },
  { id: 'incomplete_profiles', label: 'Incomplete Profiles', key: 'incomplete_profile', color: 'orange', icon: AlertTriangle },
  { id: 'deleted', label: 'Deleted', key: 'deleted', color: 'slate', icon: UserX }
];

const getTabColor = (color, isActive) => {
  const colors = {
    blue: isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200',
    emerald: isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: isActive ? 'bg-amber-600 text-white' : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200',
    purple: isActive ? 'bg-purple-600 text-white' : 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200',
    orange: isActive ? 'bg-orange-600 text-white' : 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200',
    slate: isActive ? 'bg-slate-600 text-white' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
  };
  return colors[color] || colors.blue;
};

const getIconBgColor = (color) => {
  const colors = {
    blue: 'bg-blue-100',
    emerald: 'bg-emerald-100',
    amber: 'bg-amber-100',
    purple: 'bg-purple-100',
    orange: 'bg-orange-100',
    slate: 'bg-slate-100'
  };
  return colors[color] || colors.blue;
};

const getIconColor = (color) => {
  const colors = {
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    slate: 'text-slate-600'
  };
  return colors[color] || colors.blue;
};

export const EmployeeStatsCards = ({
  stats,
  activeTab,
  onTabChange,
  onRefresh,
  loading
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-700">Employee Overview</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="text-slate-500"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = stats?.[tab.key] || 0;
          
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${getTabColor(tab.color, isActive)} ${isActive ? 'shadow-lg scale-[1.02]' : 'shadow-sm hover:shadow-md'}`}
              data-testid={`tab-${tab.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${isActive ? 'bg-white/20' : getIconBgColor(tab.color)}`}>
                  <Icon className={`w-5 h-5 ${isActive ? 'text-current' : getIconColor(tab.color)}`} />
                </div>
                {count > 0 && tab.id !== 'total' && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-current/10'}`}>
                    {count}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold">{count.toLocaleString()}</p>
              <p className={`text-xs mt-1 ${isActive ? 'text-white/80' : 'opacity-70'}`}>{tab.label}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { TABS };
export default EmployeeStatsCards;
